import { memo, useEffect, useState } from 'react'
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch'

import { v4 as uuid } from 'uuid'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Tooltip from '@ui/components/Tooltip'

import MermaidRenderer from './MermaidRenderer'
import Modal from './Modal'
interface MermaidProps {
	readonly mermaidCode: string;
	readonly isWriting?: boolean;
}

const SCALE_FACTOR = 10 // approximately 500 dpi

function MermaidWrapper({ mermaidCode, isWriting }: MermaidProps) {
	const [fullscreen, setFullscreen] = useState<boolean>(false)
	const [id, setId] = useState<string>('')

	useEffect(() => {
		setId(`m-${uuid()}`)
	}, [mermaidCode, fullscreen])

	const handleFullscreen = () => {
		setFullscreen(!fullscreen)
	}

	const handleExportPNG = (containerId: string) => {
		const svg = document.querySelector(`#${containerId} > svg`) as SVGElement

		if (!svg) {
			return
		}

		const bbox = svg.getBoundingClientRect()
		const canvas = document.createElement('canvas')
		const ctx = canvas.getContext('2d')
		const loader = new Image()

		canvas.width = bbox.width * SCALE_FACTOR
		canvas.height = bbox.height * SCALE_FACTOR

		loader.onload = () => {
			ctx?.drawImage(loader, 0, 0, canvas.width, canvas.height)

			canvas.toBlob(blob => {
				if (!blob) {
					return
				}

				const url = URL.createObjectURL(blob)
				const a = document.createElement('a')

				a.href = url
				a.download = `diagram-${containerId}.png`
				a.click()
				URL.revokeObjectURL(url)
			}, 'image/png')
		}

		const svgData = new XMLSerializer().serializeToString(svg)

		loader.src = `data:image/svg+xml,${encodeURIComponent(svgData)}`
	}

	return (
		<div>
			{ isWriting
				? (
					<div className="font-sans flex justify-center items-center p-20 my-10 border border-gray-300 rounded-md">
					Diagram loading...
					</div>
				)
				: (
					<>
						<Modal
							isOpen={ fullscreen }
							title="Diagram Explorer"
							size="xxlarge"
							onClose={ () => {
								setFullscreen(false)
							} }>
							<TransformWrapper>
								{ ({ zoomIn, zoomOut, resetTransform }) => (
									<>
										<TransformComponent
											contentStyle={ {
												height: '100%', width: '100%', minWidth: '100%', minHeight: '100%'
											} }
											wrapperStyle={ {
												height: '100%', width: '100%', minWidth: '100%', minHeight: '100%'
											} }
											wrapperClass="border border-gray-300 rounded-md">
											<div className=" w-full p-10 h-[65dvh]">
												<MermaidRenderer
													containerId={ id }
													mermaidCode={ mermaidCode } />
											</div>
										</TransformComponent>
										<div className="flex gap-2 mt-4">
											<button
												className="flex items-center gap-1 bg-blue-500 text-white px-3 py-1 rounded-md"
												onClick={ () => {
													handleExportPNG(id)
												} }>
												<FontAwesomeIcon icon="download" />
											Export PNG
											</button>
											<button
												className="flex items-center gap-1 bg-blue-500 text-white px-3 py-1 rounded-md"
												onClick={ () => {
													zoomIn()
												} }>
												<FontAwesomeIcon icon="plus" />
										Zoom
											</button>
											<button
												className="flex items-center gap-1 bg-blue-500 text-white px-3 py-1 rounded-md"
												onClick={ () => {
													zoomOut()
												} }>
												<FontAwesomeIcon icon="minus" />
										Zoom
											</button>
											<button
												className="flex items-center gap-1 bg-blue-500 text-white px-3 py-1 rounded-md"
												onClick={ () => {
													resetTransform()
												} }>
												<FontAwesomeIcon icon="rotate-left" />
										Reset
											</button>

											<button
												disabled
												data-tooltip="mouse-tooltip"
												className="flex items-center gap-1 border border-gray-300 bg-white px-3 py-1 rounded-md">
												<FontAwesomeIcon icon="arrows-up-down-left-right" />
										Move
											</button>

											<Tooltip anchor="mouse-tooltip">
												<div>
													<p className="font-light">Click and drag to move, scroll to zoom.</p>
												</div>
											</Tooltip>
										</div>
									</>
								) }
							</TransformWrapper>
						</Modal>
						<div className="relative my-10 p-10 border border-gray-300 rounded-md">
							<MermaidRenderer
								containerId={ id }
								mermaidCode={ mermaidCode }
								className="min-h-[50px]" />
							<button
								className="font-sans absolute z-20  bg-blue-500 text-white px-2 py-1 rounded-md disabled:opacity-50 top-1 left-1"
								onClick={ handleFullscreen }>
								<FontAwesomeIcon icon="up-right-and-down-left-from-center" />
							</button>
							<button
								className="absolute z-20 flex items-center gap-1 bg-blue-500 text-white px-2 py-2 rounded-md top-10 left-1"
								onClick={ () => {
									handleExportPNG(id)
								} }>
								<FontAwesomeIcon icon="download" />
							</button>
						</div>
					</>
				) }
		</div>
	)
}

export default memo(MermaidWrapper)
